import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { z } from 'zod';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import { DeliveryOutcomeCtx } from '~/features/catalog-enrichment/utils/delivery-outcome-context';
import { navContextSearchSchema } from '~/features/catalog-enrichment/utils/nav-context';

const RunOverviewPage = lazy(() => import('~/features/catalog-enrichment/pages/run-overview-page'));
const RetryOptionsSheet = lazy(() =>
  import('~/features/catalog-enrichment/components/retry-options-sheet').then((m) => ({
    default: m.RetryOptionsSheet,
  })),
);

/** `?stage=` deep-links the stepper (P0) into the overview's stages table, opening that row.
 *  `?fStage=` deep-links the mix Summary's selected stage pane (docs/run-summary-funnel.md §4) —
 *  kept distinct from `stage` so the two deep links (today's progress-card fallback and the mix)
 *  never collide. `from`/`back` (S12) — the failure-patterns view links batches straight to this tab. */
const searchSchema = z.object({
  stage: z.string().optional(),
  fStage: z.string().optional(),
  /** Opens the Retry options sheet (the old `/recovery` page redirects here). */
  retry: z.coerce.boolean().optional().catch(undefined),
  /** The step list's "problems only" filter. */
  steps: z.enum(['problems']).optional().catch(undefined),
  /** The Publish step's delivered-products outcome filter. */
  dout: z.string().optional().catch(undefined),
  ...navContextSearchSchema,
});

export const Route = createFileRoute('/_authenticated/_tenant/catalog-enrichment/runs/$runId/')({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const { runId } = Route.useParams();
  const { stage, fStage, retry, steps, dout } = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <DeliveryOutcomeCtx.Provider
      value={{
        value: dout ?? null,
        onChange: (next) => {
          void navigate({ search: (prev) => ({ ...prev, dout: next ?? undefined }) });
        },
      }}
    >
      <Suspense fallback={<PageSkeleton />}>
        <RunOverviewPage
          runId={runId}
          expandStage={stage ?? null}
          selectedStage={fStage ?? null}
          // Picking a step, the problems filter and the retry sheet each push a history entry.
          onSelectedStageChange={(next) => {
            void navigate({ search: (prev) => ({ ...prev, fStage: next ?? undefined }) });
          }}
          problemsOnly={steps === 'problems'}
          onProblemsOnlyChange={(on) => {
            void navigate({ search: (prev) => ({ ...prev, steps: on ? 'problems' : undefined }) });
          }}
        />
        <RetryOptionsSheet
          runId={runId}
          open={!!retry}
          onOpenChange={(open) => {
            void navigate({
              search: (prev) => ({ ...prev, retry: open ? true : undefined }),
            });
          }}
        />
      </Suspense>
    </DeliveryOutcomeCtx.Provider>
  );
}
